<template>
    <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
        <div
            class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
            <div class="d-flex flex-wrap align-items-end gap-3">
                <div>
                    <label class="form-label fs-13 mb-5">Année oct.–déc. (ex. 2025)</label>
                    <input v-model.number="anneeOct" type="number" class="form-control" style="max-width: 140px"
                        min="2000" max="2100" />
                </div>
                <div>
                    <label class="form-label fs-13 mb-5">Année janv.–mai (ex. 2026)</label>
                    <input
                        v-model.number="anneeJanMai"
                        type="number"
                        class="form-control"
                        style="max-width: 140px"
                        min="2000"
                        max="2100"
                        @change="onAnneeJanMaiChange"
                    />
                </div>
                <div>
                    <label class="form-label fs-13 mb-5">Date limite de paiement (optionnel)</label>
                    <input
                        v-model="dateLimitePaiement"
                        type="date"
                        class="form-control"
                        style="max-width: 200px"
                    />
                </div>
                <div>
                    <label class="form-label fs-13 mb-5">Fichier Excel (.xlsx)</label>
                    <input type="file" class="form-control" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        @change="onFile" />
                </div>
                <button class="btn btn-primary align-self-end" type="button" :disabled="!fichier || loading"
                    @click="importer">
                    {{ loading ? "Traitement…" : "Générer les factures" }}
                </button>
                <button
                    v-if="facturesCreees.length && factureIdsAnnulables.length"
                    type="button"
                    class="btn btn-outline-danger align-self-end"
                    :disabled="loading"
                    @click="annulerDernierLotFactures"
                >
                    Annuler ce lot ({{ factureIdsAnnulables.length }} facture(s))
                </button>
            </div>
            <p class="text-body-secondary fs-13 mb-0 mt-10 px-15 px-sm-20 px-md-25">
                Les libellés et la période de facture utilisent ces deux années : par exemple
                <strong>oct.–déc. 2025</strong> et <strong>janv.–mai 2026</strong>.
                Par défaut, janv.–mai = oct.–déc. + 1 ; modifiez la deuxième année si besoin.
                Sans date limite renseignée, la date est calculée comme pour les autres factures (règle métier serveur).
            </p>
        </div>

        <div class="card-body p-15 p-sm-20 p-md-25" v-if="facturesCreees.length || lignesRejetees.length">
            <h6 class="fw-medium mb-15">Factures créées ({{ facturesCreees.length }})</h6>
            <div class="table-responsive mb-25" v-if="facturesCreees.length">
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th class="text-uppercase fs-13">Id</th>
                            <th class="text-uppercase fs-13">Référence</th>
                            <th class="text-uppercase fs-13">Code place</th>
                            <th class="text-uppercase fs-13">Montant</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(s, i) in facturesCreees" :key="'s-' + i">
                            <td>{{ s.id }}</td>
                            <td>{{ s.reference }}</td>
                            <td>{{ s.codePlace }}</td>
                            <td>{{ s.montant }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <h6 class="fw-medium mb-15 text-danger" v-if="lignesRejetees.length">Rejets ({{ lignesRejetees.length }})</h6>
            <div class="table-responsive" v-if="lignesRejetees.length">
                <table class="table text-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th class="text-uppercase fs-13">Code place</th>
                            <th class="text-uppercase fs-13">Motif</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(r, i) in lignesRejetees" :key="'r-' + i">
                            <td>{{ r.codePlace }}</td>
                            <td>{{ r.motif }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";
import { error, success as toastSuccess } from "@/utils/utils";

type FactureOk = { id: number; reference: string; codePlace: string; montant: number };
type LigneRejet = { codePlace?: string; motif?: string };

function defaultAnneeOctDec(): number {
    const d = new Date();
    const y = d.getFullYear();
    return d.getMonth() < 9 ? y - 1 : y;
}

export default defineComponent({
    name: "ImportationFacturesElectriciteExcel",
    setup() {
        const fichier = ref<File | null>(null);
        const anneeOct = ref(defaultAnneeOctDec());
        const anneeJanMai = ref(anneeOct.value + 1);
        const anneeJanMaiManuelle = ref(false);
        /** Vide = le backend applique recupererDateLimite ; sinon AAAA-MM-JJ */
        const dateLimitePaiement = ref("");
        const loading = ref(false);
        const facturesCreees = ref<FactureOk[]>([]);
        const lignesRejetees = ref<LigneRejet[]>([]);
        const factureIdsAnnulables = ref<number[]>([]);

        const onFile = (e: Event) => {
            const t = e.target as HTMLInputElement;
            fichier.value = t.files?.[0] ?? null;
        };

        const onAnneeJanMaiChange = () => {
            anneeJanMaiManuelle.value = true;
        };

        watch(anneeOct, (v) => {
            if (!anneeJanMaiManuelle.value) {
                const o = Number(v);
                anneeJanMai.value = (Number.isFinite(o) ? o : defaultAnneeOctDec()) + 1;
            }
        });

        const annulerDernierLotFactures = async () => {
            const ids = factureIdsAnnulables.value;
            if (!ids.length) return;
            const r = await Swal.fire({
                icon: "warning",
                title: "Supprimer ces factures ?",
                html: `Les <strong>${ids.length}</strong> facture(s) du dernier import Excel seront annulées (suppression logique + PDF).`,
                showCancelButton: true,
                confirmButtonText: "Annuler le lot",
                cancelButtonText: "Fermer",
                confirmButtonColor: "#d33",
            });
            if (!r.isConfirmed) return;
            try {
                ApiService.setHeader();
                const res = await ApiService.post("importation/factures-electricite-specifique/annuler", {
                    factureIds: ids,
                });
                if (res.status === 200) {
                    facturesCreees.value = [];
                    factureIdsAnnulables.value = [];
                    toastSuccess(res.data.message || "Factures annulées.");
                }
            } catch (err: unknown) {
                const ax = err as { response?: { data?: { message?: string } }; message?: string };
                error(ax?.response?.data?.message || ax?.message || "Annulation impossible.");
            }
        };

        const importer = async () => {
            if (!fichier.value) {
                Swal.fire({ icon: "error", title: "Erreur", text: "Choisissez un fichier .xlsx." });
                return;
            }
            loading.value = true;
            facturesCreees.value = [];
            lignesRejetees.value = [];
            factureIdsAnnulables.value = [];
            try {
                ApiService.setHeader();
                const fd = new FormData();
                fd.append("file", fichier.value);
                fd.append("anneeOct", String(anneeOct.value ?? defaultAnneeOctDec()));
                fd.append("anneeJanMai", String(anneeJanMai.value ?? (Number(anneeOct.value) + 1)));
                if (String(dateLimitePaiement.value ?? "").trim()) {
                    fd.append("dateLimitePaiement", String(dateLimitePaiement.value).trim());
                }
                const response = await ApiService.postFormData(
                    "importation/factures-electricite-specifique",
                    fd
                );
                if (response.status === 200 && response.data?.data) {
                    const ok = (response.data.data.succes || []) as FactureOk[];
                    facturesCreees.value = ok;
                    factureIdsAnnulables.value = ok.map((x) => x.id).filter((id) => typeof id === "number");
                    lignesRejetees.value = response.data.data.rejets || [];
                    toastSuccess(response.data.message || "Import terminé.");
                }
            } catch (err: unknown) {
                const ax = err as { response?: { data?: { message?: string } }; message?: string };
                error(ax?.response?.data?.message || ax?.message || "Erreur lors de l'import.");
            } finally {
                loading.value = false;
            }
        };

        return {
            fichier,
            anneeOct,
            anneeJanMai,
            dateLimitePaiement,
            onAnneeJanMaiChange,
            loading,
            facturesCreees,
            lignesRejetees,
            factureIdsAnnulables,
            onFile,
            importer,
            annulerDernierLotFactures,
        };
    },
});
</script>
